import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import './ChatHistory.css';
import config from '../config';

const ChatHistory = forwardRef(({ onSelectConversation, currentThreadId, onClose }, ref) => {
    const [conversations, setConversations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const backendIp = config.backendIp;

    useEffect(() => {
        loadConversations();
    }, []);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const savedIds = JSON.parse(localStorage.getItem('threadIds') || '[]');
            if (savedIds.length === 0) {
                setConversations([]);
                return;
            }
            // Load and filter valid threads
            const conversationPromises = savedIds.map(async (threadId) => {
                try {
                    const threadResponse = await fetch(`${backendIp}/api/threads/${threadId}`);
                    if (!threadResponse.ok) return null;
                    const threadData = await threadResponse.json();
                    const thread = threadData.thread;
                    const messages = threadData.messages;
                    const firstUserMessage = messages.find(msg => msg.is_user === 'user');
                    const preview = firstUserMessage ?
                        firstUserMessage.content.substring(0, 100) + (firstUserMessage.content.length > 100 ? '...' : '') :
                        'New conversation';
                    const title = firstUserMessage ?
                        firstUserMessage.content.substring(0, 50) + (firstUserMessage.content.length > 50 ? '...' : '') :
                        `Chat ${new Date(thread.created_at).toLocaleDateString()}`;
                    return {
                        threadId: thread.id,
                        title,
                        lastUpdated: thread.created_at,
                        messageCount: messages.length,
                        preview
                    };
                } catch (error) {
                    return null;
                }
            });
            const results = await Promise.all(conversationPromises);
            // Filter out missing threads
            const validConversations = results.filter(item => item !== null);
            // Update stored IDs to drop missing ones
            const validIds = validConversations.map(item => item.threadId);
            if (validIds.length !== savedIds.length) {
                localStorage.setItem('threadIds', JSON.stringify(validIds));
            }
            setConversations(validConversations.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated)));
        } catch (error) {
            setConversations([]);
        } finally {
            setLoading(false);
        }
    };

    const saveConversation = (threadId, title, messages) => {
        // Note: Conversations are now automatically saved by the backend
        // This function is kept for compatibility but just refreshes the list
        if (process.env.NODE_ENV === 'development') {
            console.log('[ChatHistory] Conversation saved by backend, refreshing list:', {
                threadId,
                title,
                messageCount: messages.length,
                timestamp: new Date().toISOString()
            });
        }
        
        // Refresh conversations from backend to get latest data
        loadConversations();
    };

    const deleteConversation = async (threadId, e) => {
        e.stopPropagation();
        
        if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
            return;
        }
        
        try {
            // Note: Backend doesn't have delete endpoint yet, so just remove from local state
            // In a full implementation, you'd call: await fetch(`${backendIp}/api/threads/${threadId}`, { method: 'DELETE' })
            const updated = conversations.filter(conv => conv.threadId !== threadId);
            setConversations(updated);
            
            // If we're currently viewing this conversation, start a new one
            if (threadId === currentThreadId) {
                onSelectConversation('new');
            }
            
        } catch (error) {
            // Error deleting conversation
        }
    };

    const clearAllHistory = () => {
        if (window.confirm('Are you sure you want to clear all chat history? This action cannot be undone.')) {
            // Note: Backend doesn't have bulk delete endpoint yet, so just clear local state
            // In a full implementation, you'd call the backend to delete all threads
            setConversations([]);
            
            // If we're currently viewing a conversation, start a new one
            if (currentThreadId !== 'new') {
                onSelectConversation('new');
            }
            
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Today';
        if (diffDays === 2) return 'Yesterday';
        if (diffDays <= 7) return `${diffDays - 1} days ago`;
        return date.toLocaleDateString();
    };

    const filteredConversations = conversations.filter(conv =>
        conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conv.preview.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelectConversation = (threadId) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('[ChatHistory] Selecting conversation:', {
                threadId,
                timestamp: new Date().toISOString()
            });
        }
        onSelectConversation(threadId);
        onClose();
    };

    const handleNewChat = () => {
        onSelectConversation('new');
        onClose();
    };

    // Expose saveConversation method to parent component
    useImperativeHandle(ref, () => ({
        saveConversation
    }));

    return (
        <div className="chat-history-overlay" onClick={onClose}>
            <div className="chat-history-modal" onClick={(e) => e.stopPropagation()}>
                <div className="chat-history-header">
                    <h2 className="chat-history-title">Chat History</h2>
                    <button 
                        className="chat-history-close"
                        onClick={onClose}
                        title="Close"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="chat-history-content">
                    <div className="chat-history-actions">
                        <button
                            className="new-chat-button"
                            onClick={handleNewChat}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            New Chat
                        </button>
                        {conversations.length > 0 && (
                            <button
                                className="clear-history-button"
                                onClick={clearAllHistory}
                                title="Clear all chat history"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3,6 5,6 21,6"></polyline>
                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                                Clear All History
                            </button>
                        )}
                    </div>

                    <div className="history-search">
                        <input
                            type="text"
                            className="history-search-input"
                            placeholder="Search conversations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="chat-history-list">
                        {loading ? (
                            <div className="history-empty">
                                <div className="history-empty-title">Loading conversations...</div>
                            </div>
                        ) : filteredConversations.length === 0 ? (
                            <div className="history-empty">
                                <div className="history-empty-title">
                                    {searchTerm ? 'No matching conversations' : 'No chat history yet'}
                                </div>
                                <div className="history-empty-description">
                                    {searchTerm ? 'Try a different search term' : 'Start a conversation to see it here'}
                                </div>
                            </div>
                        ) : (
                            filteredConversations.map((conv) => (
                                <div 
                                    key={conv.threadId}
                                    className={`history-item ${conv.threadId === currentThreadId ? 'active' : ''}`}
                                    onClick={() => handleSelectConversation(conv.threadId)}
                                >
                                    <div className="history-item-content">
                                        <div className="history-item-title">{conv.title}</div>
                                        <div className="history-item-preview">{conv.preview}</div>
                                        <div className="history-item-meta">
                                            <span className="history-item-date">{formatDate(conv.lastUpdated)}</span>
                                            <span className="history-item-count">{conv.messageCount} messages</span>
                                        </div>
                                    </div>
                                    <div className="history-item-actions">
                                        <button 
                                            className="history-action-button delete"
                                            onClick={(e) => deleteConversation(conv.threadId, e)}
                                            title="Delete conversation"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3,6 5,6 21,6"></polyline>
                                                <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {filteredConversations.length > 0 && (
                    <div className="history-stats">
                        {filteredConversations.length} of {conversations.length} conversations
                    </div>
                )}
            </div>
        </div>
    );
});

ChatHistory.displayName = 'ChatHistory';

export default ChatHistory;